import Dexie, { type Table } from 'dexie';
import type { Design } from '@/domain/model/types';

/**
 * IndexedDB schema (docs/09 "IndexedDB stores"). The database version is independent
 * of the project schema, archive, catalog, and app versions. Never clear the database
 * to "fix" an upgrade: add a new `version(n)` with an upgrade function and tests.
 */
export const DATABASE_NAME = 'jamies-nails-pro';
export const DATABASE_VERSION = 1;

export interface ProjectRow {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly revision: number;
  readonly schemaVersion: number;
  readonly nailCount: number;
  readonly designCount: number;
  /** Set when moved to the recoverable trash; null when active. */
  readonly deletedAt: string | null;
}

export interface DocumentRow {
  readonly id: string;
  /** Validated canonical project document (plain JSON-compatible data). */
  readonly document: unknown;
}

export interface ThumbnailRow {
  readonly projectId: string;
  readonly revision: number;
  readonly svg: string;
}

export interface SnapshotRow {
  readonly seq?: number;
  readonly projectId: string;
  readonly createdAt: string;
  readonly reason:
    'periodic' | 'before-migration' | 'before-replace' | 'before-import-replace' | 'manual';
  readonly revision: number;
  readonly bytes: number;
  readonly document: unknown;
}

export interface PreferenceRow {
  readonly key: string;
  readonly value: unknown;
}

export interface FavoriteRow {
  readonly catalogId: string;
  readonly addedAt: string;
}

export interface CollectionItem {
  readonly kind: 'catalog' | 'personal-template';
  readonly id: string;
}

export interface CollectionRow {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly items: readonly CollectionItem[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PersonalTemplateRow {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly design: Design;
}

export interface RecentRow {
  readonly key: string;
  readonly usedAt: string;
}

export interface PackRow {
  readonly packId: string;
  readonly packVersion: string;
  readonly downloadedAt: string;
  readonly totalBytes: number;
  readonly fileCount: number;
}

export interface PackFileRow {
  /** `${packId}@${packVersion}/${path}` */
  readonly key: string;
  readonly packId: string;
  readonly packVersion: string;
  readonly content: string;
}

export class JnpDatabase extends Dexie {
  declare projects: Table<ProjectRow, string>;
  declare documents: Table<DocumentRow, string>;
  declare thumbnails: Table<ThumbnailRow, string>;
  declare snapshots: Table<SnapshotRow, number>;
  declare preferences: Table<PreferenceRow, string>;
  declare favorites: Table<FavoriteRow, string>;
  declare collections: Table<CollectionRow, string>;
  declare personalTemplates: Table<PersonalTemplateRow, string>;
  declare recents: Table<RecentRow, string>;
  declare packs: Table<PackRow, [string, string]>;
  declare packFiles: Table<PackFileRow, string>;

  constructor(name: string = DATABASE_NAME) {
    super(name);
    this.version(DATABASE_VERSION).stores({
      projects: 'id, updatedAt',
      documents: 'id',
      thumbnails: 'projectId',
      snapshots: '++seq, projectId, [projectId+createdAt]',
      preferences: 'key',
      favorites: 'catalogId, addedAt',
      collections: 'id, updatedAt',
      personalTemplates: 'id, updatedAt',
      recents: 'key, usedAt',
      packs: '[packId+packVersion], packId',
      packFiles: 'key, [packId+packVersion]',
    });
  }
}

let instance: JnpDatabase | null = null;

/** The shared database. Opening errors surface on first use and are classified by callers. */
export function getDatabase(): JnpDatabase {
  instance ??= new JnpDatabase();
  return instance;
}

/** Test hook: replace the shared database (e.g. a uniquely named fake-indexeddb instance). */
export function setDatabaseForTesting(db: JnpDatabase | null): void {
  instance = db;
}
